import { Box, Paper, Typography, alpha } from '@mui/material';

import { useDragDropWrapper, type DragDropTexts } from './hooks/useDragDropWrapper';
import { Icon } from '../../mui-wrapper-components/Icon/Icon';

type ChatWindowDragDropWrapperProps = {
  maxFileSizeMB?: number;
  accept?: string;
  onFileSelected?: (file: File) => void;
  isReady: boolean;
  children: React.ReactNode;
  dragDropTexts?: DragDropTexts;
  fileError: React.ReactNode | null;
  setFileError: (error: React.ReactNode | null) => void;
};

export const ChatWindowDragDropWrapper = ({
  onFileSelected,
  isReady,
  children,
  maxFileSizeMB = 10,
  accept = '.csv',
  dragDropTexts,
  fileError,
  setFileError,
}: ChatWindowDragDropWrapperProps) => {
  const { getRootProps, getInputProps, isDragActive, isDragReject, allowedFileTypes } =
    useDragDropWrapper({
      maxFileSizeMB,
      accept,
      onFileSelected,
      isReady,
      dragDropTexts,
      fileError,
      setFileError,
    });

  const dragRejectTitle = dragDropTexts?.dragRejectTitle ?? 'Invalid file type or size';
  const dragActiveTitle = dragDropTexts?.dragActiveTitle ?? 'Drop your file here';
  const dragRejectDescription =
    dragDropTexts?.dragRejectDescription ??
    `Allowed: ${allowedFileTypes.join(', ')} (max ${maxFileSizeMB}MB)`;
  const dragActiveDescription =
    dragDropTexts?.dragActiveDescription ??
    `${allowedFileTypes.join(', ')} files up to ${maxFileSizeMB}MB`;

  return (
    <Box
      {...getRootProps()}
      sx={{
        position: 'relative',
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <input {...getInputProps()} />

      {/* Drag overlay */}
      {isDragActive && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 1000,
            // TODO: no VDS token for this value — needs designer input (#ff0000 reject, #ccf0f9 accept drag colors)
            backgroundColor: alpha(isDragReject ? '#ff0000' : '#ccf0f9', 0.9),
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            pointerEvents: 'none',
          }}
        >
          <Paper
            elevation={8}
            sx={{
              p: 4,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 2,
              backgroundColor: 'white',
            }}
          >
            <Icon
              id="upload-icon"
              icon={'upload'}
              width={64}
              height={64}
              sx={{ color: isDragReject ? 'error.main' : 'primary.main' }}
            />
            <Typography variant="h5" textAlign="center">
              {isDragReject ? dragRejectTitle : dragActiveTitle}
            </Typography>
            <Typography variant="body2" color="text.secondary" textAlign="center">
              {isDragReject ? dragRejectDescription : dragActiveDescription}
            </Typography>
          </Paper>
        </Box>
      )}
      {/* Chat window */}
      {children}
    </Box>
  );
};
